import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { Microphone, Stop, CircleNotch } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pickMime = () => {
  if (window.MediaRecorder?.isTypeSupported?.('audio/webm')) return { mime: 'audio/webm', ext: 'webm' };
  if (window.MediaRecorder?.isTypeSupported?.('audio/mp4')) return { mime: 'audio/mp4', ext: 'mp4' };
  return { mime: '', ext: 'webm' };
};

/**
 * Tap-to-record voice dictation button (EN/ES auto-detect).
 * mode="tasks" → AI splits speech into task items; mode="note" → verbatim transcript.
 * onResult({ transcript, language, tasks }) fires on success.
 * compact=true renders an icon-only square button.
 */
export const VoiceDictateButton = ({ mode = 'note', onResult, compact = false, className = '' }) => {
  const [state, setState] = useState('idle'); // idle | recording | processing
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const extRef = useRef('webm');

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const { mime, ext } = pickMime();
      extRef.current = ext;
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => send();
      rec.start();
      recRef.current = rec;
      setState('recording');
    } catch {
      toast({ title: 'Microphone blocked', description: 'Allow microphone access in your browser to dictate.', variant: 'destructive' });
    }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch { /* noop */ }
    streamRef.current?.getTracks().forEach((t) => t.stop());
  };

  const send = async () => {
    setState('processing');
    try {
      const blob = new Blob(chunksRef.current, { type: recRef.current?.mimeType || 'audio/webm' });
      if (blob.size < 1000) {
        toast({ title: 'Too short', description: 'Hold on a bit longer and speak clearly.', variant: 'destructive' });
        return;
      }
      const form = new FormData();
      form.append('audio', blob, `speech.${extRef.current}`);
      form.append('mode', mode);
      const res = await axios.post(`${API}/daily-checklists/voice-transcribe`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.data?.success || !res.data?.transcript) {
        toast({ title: "Couldn't hear that", description: 'Try again, speaking clearly into the mic.', variant: 'destructive' });
      } else {
        onResult?.(res.data);
      }
    } catch (e) {
      toast({ title: 'Dictation failed', description: e?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setState('idle');
    }
  };

  const onClick = () => {
    if (state === 'recording') stop();
    else if (state === 'idle') start();
  };

  const recording = state === 'recording';
  const processing = state === 'processing';

  const icon = processing
    ? <CircleNotch size={compact ? 14 : 16} weight="bold" className="animate-spin" />
    : recording
      ? <Stop size={compact ? 14 : 16} weight="fill" />
      : <Microphone size={compact ? 14 : 16} weight="bold" />;

  const base = recording
    ? 'bg-red-600 text-white animate-pulse'
    : processing
      ? 'bg-neutral-200 dark:bg-neutral-700 dark:text-white'
      : 'bg-black text-white hover:bg-neutral-800';

  if (compact) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={processing}
        title={recording ? 'Stop recording' : 'Dictate (EN/ES)'}
        className={`w-8 h-8 flex items-center justify-center border-2 border-black rounded-none transition-colors disabled:opacity-60 ${base} ${className}`}
        data-testid={`voice-dictate-btn-${mode}`}
      >
        {icon}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={processing}
      className={`inline-flex items-center gap-2 h-9 px-3 font-black uppercase text-xs tracking-widest border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none rounded-none transition-colors disabled:opacity-60 ${base} ${className}`}
      data-testid={`voice-dictate-btn-${mode}`}
    >
      {icon}
      {processing ? 'Transcribing…' : recording ? 'Stop' : mode === 'tasks' ? 'Dictate Tasks' : 'Dictate'}
    </button>
  );
};

export default VoiceDictateButton;
